import { createContext, useContext, useId, type ReactNode } from 'react';

import { cn } from '../lib/cn';

import { EmptyState } from './empty-state';
import { ScrollArea } from './scroll-area';

/* ------------------------------------------------- the two layouts, and their names ---- */

/** Which of the `Table`'s two renderings a cell is in. See the note on `Table` itself. */
export type TableLayout = 'table' | 'cards';

const TableLayoutContext = createContext<TableLayout | null>(null);

/** The layout the calling component is being rendered in, or `null` outside a `Table`. */
export function useTableLayout(): TableLayout | null {
  return useContext(TableLayoutContext);
}

/**
 * A `name` that is unique to the layout it is rendered in.
 *
 * **Why this exists.** `Table` puts *both* layouts in the DOM and hides one with CSS. A
 * radio group inside a cell with a fixed `name` therefore becomes **one** browser radio
 * group spanning both copies — and a radio group holds one checked radio, so the visible
 * copy shows nothing selected. The super console hit exactly this on the tenant feature
 * overrides (`apps/super/src/features/tenants/features-tab.tsx`) and worked around it by
 * extracting a component so that `useId()` would differ per instance.
 *
 * This is that workaround, named, so the next caller does not have to rediscover it:
 *
 * ```tsx
 * function OverrideChoice({ feature }: { feature: TenantFeature }) {
 *   const name = useLayoutScopedName(feature.key);
 *   return <Radio name={name} … />;
 * }
 * ```
 *
 * A cell that renders inline instead of through a component cannot call a hook; that case
 * is served by the second argument to `TableColumn.render`, which carries the same
 * `scopedName`.
 */
export function useLayoutScopedName(base: string): string {
  const id = useId();
  const layout = useContext(TableLayoutContext);
  return scopedName(id, layout ?? 'standalone', base);
}

/** One place builds the string, so the hook and the render argument cannot drift. */
function scopedName(scope: string, layout: string, base: string): string {
  return `${scope}-${layout}-${base}`;
}

/** What a cell is told about where it is being drawn. Additive: ignore it and nothing changes. */
export interface TableCellContext {
  /** `'table'` for the ≥768px table, `'cards'` for the small-screen card list. */
  layout: TableLayout;
  /**
   * A form-control `name` unique to this table, this layout and this row — the fix for
   * the duplicate radio group described on `useLayoutScopedName`.
   */
  scopedName: (base: string) => string;
}

export interface TableColumn<Row> {
  /** Stable key, also used as the label on the small-screen card layout. */
  key: string;
  header: ReactNode;
  /**
   * Defaults to `String(row[key])` when the row is an object with that key.
   *
   * `cell` says which of the two layouts this call is drawing and hands over a
   * `scopedName` for any radio or checkbox group inside the cell.
   */
  render?: (row: Row, cell: TableCellContext) => ReactNode;
  align?: 'left' | 'right';
  /** Right-aligned tabular figures for money and counts (tokens doc §10.3). */
  numeric?: boolean;
  /** Hide this column from the small-screen card layout — for row actions, say. */
  hideOnCard?: boolean;
}

export interface TableProps<Row> {
  columns: TableColumn<Row>[];
  rows: Row[];
  getRowKey: (row: Row, index: number) => string;
  /** Accessible name for the table. Required: a table with no caption is unnavigable. */
  caption: string;
  /** Rendered in place of the rows when `rows` is empty (rules §17.4). */
  empty?: ReactNode;
  /** Highlights a row with `--color-primary-subtle`. */
  isRowSelected?: (row: Row) => boolean;
  className?: string;
}

function cellContent<Row>(column: TableColumn<Row>, row: Row, cell: TableCellContext): ReactNode {
  if (column.render !== undefined) return column.render(row, cell);
  const value = (row as Record<string, unknown>)[column.key];
  return value === null || value === undefined ? '—' : String(value);
}

/**
 * The admin table (tokens doc §10.3). The wrapper is the card recipe with no padding —
 * the cells own it. Header row on `--color-table-header`, uppercase 11px neutral-400;
 * body rows separated by the lighter neutral-100 hairline.
 *
 * **Small screens (FR-UI-05, rules §49.4).** Below 768px the real `<table>` is hidden
 * and each row becomes a card of label–value pairs, reusing the `/payment` `.info-grid`
 * pattern rather than inventing a responsive table. Both layouts are in the DOM and the
 * swap is pure CSS, so there is no resize listener and no layout flash; the inactive one
 * is `display: none`, which also removes it from the accessibility tree, so a screen
 * reader never meets the same row twice.
 *
 * **The one thing that leaks.** `display: none` hides a row from the reader but not from
 * the *browser*, which still groups radios by `name` across both copies. A cell with a
 * radio or checkbox group must therefore take its `name` from the `scopedName` on its
 * `render`'s second argument, or from `useLayoutScopedName()` inside a cell component —
 * see the note on that hook.
 */
export function Table<Row>({
  columns,
  rows,
  getRowKey,
  caption,
  empty,
  isRowSelected,
  className,
}: TableProps<Row>) {
  const emptyState = empty ?? <EmptyState title="Nothing to show yet" />;
  const cardColumns = columns.filter((column) => column.hideOnCard !== true);
  const scope = useId();

  const cellContextFor = (layout: TableLayout, rowKey: string): TableCellContext => ({
    layout,
    scopedName: (base) => scopedName(scope, layout, `${rowKey}-${base}`),
  });

  return (
    <div
      className={cn(
        'overflow-hidden rounded-card border border-neutral-200 bg-surface shadow-card',
        className,
      )}
    >
      {/* ---- desktop: a real table ---- */}
      {/* `md:` is 768px. Written out literally, never composed at runtime: Tailwind
          scans source text, so a class built from a template string is never generated.
          `ScrollArea` (M11.11) makes the sideways scroll reachable from the keyboard,
          shows a fade where content continues, and stops the absolutely positioned
          `sr-only` caption from leaking a page-wide horizontal scrollbar. */}
      <TableLayoutContext.Provider value={TABLE_LAYOUT}>
        <ScrollArea label={caption} wrapperClassName="hidden md:block">
          <table className="w-full border-collapse text-left">
            <caption className="sr-only">{caption}</caption>
            <thead>
              <tr className="bg-table-header">
                {columns.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    className={cn(
                      'border-b border-neutral-200 px-4 py-[10px] text-2xs font-medium tracking-badge text-neutral-400-text uppercase',
                      column.align === 'right' || column.numeric === true
                        ? 'text-right'
                        : 'text-left',
                    )}
                  >
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length}>{emptyState}</td>
                </tr>
              ) : (
                rows.map((row, index) => {
                  const rowKey = getRowKey(row, index);
                  const cell = cellContextFor('table', rowKey);
                  const selected = isRowSelected?.(row) === true;
                  return (
                    /*
                     * M13.7. No `aria-selected`: it is only supported on a row inside a
                     * `grid` or `treegrid`, and on a plain `<table>` axe reports it as an
                     * attribute the role does not allow — which also meant the selected
                     * state reached a screen reader through nothing but the tint (WCAG
                     * 1.4.1 Use of Color). The state is now a word in the first cell,
                     * visible to a reader and invisible on screen, and the tint stays.
                     */
                    <tr
                      key={rowKey}
                      className={cn(
                        'border-b border-neutral-100 last:border-b-0',
                        selected ? 'bg-primary-subtle' : 'hover:bg-neutral-50',
                      )}
                    >
                      {columns.map((column, columnIndex) => (
                        <td
                          key={column.key}
                          className={cn(
                            'px-4 py-3 text-base text-ink',
                            column.align === 'right' || column.numeric === true
                              ? 'text-right'
                              : 'text-left',
                            column.numeric === true && 'tabular-nums',
                          )}
                        >
                          {selected && columnIndex === 0 ? (
                            <span className="sr-only">Selected. </span>
                          ) : null}
                          {cellContent(column, row, cell)}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </ScrollArea>
      </TableLayoutContext.Provider>

      {/* ---- below 768px: one card per row, the /payment .info-grid pattern ---- */}
      <TableLayoutContext.Provider value={CARDS_LAYOUT}>
        <div className="block md:hidden" data-testid="table-cards">
          {rows.length === 0 ? (
            emptyState
          ) : (
            <ul aria-label={caption} className="divide-y divide-neutral-100">
              {rows.map((row, index) => {
                const selected = isRowSelected?.(row) === true;
                const rowKey = getRowKey(row, index);
                const cell = cellContextFor('cards', rowKey);
                return (
                  <li key={rowKey} className={cn('px-4 py-4', selected && 'bg-primary-subtle')}>
                    {/* M13.7: the same word the table layout puts in its first cell, so
                    the tint is never the only carrier of "selected" (WCAG 1.4.1). */}
                    {selected ? <span className="sr-only">Selected. </span> : null}
                    {/* One column on a phone, two from 480px: a label–value pair in a
                    155px column wraps a booking reference onto four lines (M11.11). */}
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-3 min-[480px]:grid-cols-2">
                      {cardColumns.map((column) => (
                        <div key={column.key} className="min-w-0">
                          {/* The muted label steps a shade darker on a selected row:
                          `--color-neutral-400-text` is 4.76:1 on white but only 4.34:1
                          on `--color-primary-subtle` (M11.11). */}
                          <dt
                            className={cn(
                              'text-2xs font-medium tracking-badge uppercase',
                              selected ? 'text-neutral-600' : 'text-neutral-400-text',
                            )}
                          >
                            {column.header}
                          </dt>
                          <dd
                            className={cn(
                              'mt-[2px] text-base text-ink',
                              column.numeric === true && 'tabular-nums',
                            )}
                          >
                            {cellContent(column, row, cell)}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </TableLayoutContext.Provider>
    </div>
  );
}

/* Stable references, so the two providers never re-render their subtrees for nothing. */
const TABLE_LAYOUT: TableLayout = 'table';
const CARDS_LAYOUT: TableLayout = 'cards';
